@let showTagText = message.content | tagTextVisible;
@let isRichContent = message.content | messageContent;
@let typeName = message.messageType | messageTypeName;

<div class="flex items-start gap-[12px] w-full" [class.flex-row-reverse]="isSender">
  <!-- 头像（纯展示） -->
  <div class="shrink-0">
    <lib-user-avatar
      [firstName]="message.firstName"
      [src]="message.avatar"
      [size]="32"
      [shape]="'circle'"
      [type]="isSender ? 'teacher' : 'student'"
      [enableTarget]="false"
    ></lib-user-avatar>
  </div>

  <!-- 消息主体 -->
  <div class="flex flex-col min-w-0 max-w-[70%]" [class.items-end]="isSender">
    <!-- 姓名栏（含唯一插槽 nameSlot） -->
    @if (!isSender) {
      <div class="flex items-center gap-[6px] mb-[4px]">
        <span class="text-[12px] text-neutral-n3">{{ message.firstName }}</span>
        <ng-content select="[nameSlot]"></ng-content>
      </div>
    }

    <!-- 行：左外侧进度环 + 气泡（进度环仅本人消息，位于气泡左侧、垂直最下方） -->
    <div class="flex items-end w-full" [class.justify-end]="isSender">
      <!-- 已读进度环（空心圆外圈 + 中心按状态切换：灰边无内容 / 绿边饼图按占比 / 绿边对勾，仅本人消息） -->
      @if (isSender && message.totalCount > 0) {
        <div
          class="relative shrink-0 mr-[8px] mb-[2px] size-[14px] rounded-full overflow-hidden border-[1.5px] text-[#10A993]"
          [class.border-neutral-n4]="!(message.hasReadCount > 0)"
          [class.border-[#10A993]]="message.hasReadCount > 0"
        >
          @if (message.hasReadCount > 0 && message.hasReadCount < message.totalCount) {
            <div class="w-full h-full" [style.background]="message.hasReadCount | readRing: message.totalCount"></div>
          } @else if (message.hasReadCount >= message.totalCount) {
            <svg viewBox="0 0 16 16" class="w-full h-full">
              <path
                d="M4.5 8.5l2.5 2.5 4.5-5.5"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          }
        </div>
      }

      <!-- 气泡（非本人可点击回复；点击避开内部交互子元素） -->
      <div
        class="flex flex-col gap-[6px] flex-1 min-w-0 rounded-[8px] px-[12px] py-[8px] text-[14px] leading-[1.6] break-words whitespace-pre-wrap"
        [class.bg-neutral-n9]="!isSender"
        [class.bg-primary-b7]="isSender"
        [class.cursor-pointer]="!isSender"
        (click)="onBubbleClick($event)"
      >
        <!-- 引用块 -->
        @if (parentMessage) {
          <div class="message-quote flex items-center gap-[8px] text-[12px] text-neutral-n3">
            <span class="h-[10px] w-[2px] shrink-0 bg-neutral-n4"></span>
            <fl-message-summary
              class="min-w-0 flex-1 truncate"
              [summary]="{
                label: '回复',
                firstName: parentMessage.firstName,
                content: parentMessage.content,
                tags: parentMessage.tags,
                messageType: parentMessage.messageType,
                mediaUrls: parentMessage.mediaUrls
              }"
            ></fl-message-summary>
          </div>
        }

        <!-- 消息类型 + 纯文本正文（同行排布）：有正文时类型只留 emoji 与正文同行，无正文时类型独占一行 -->
        @if (typeName || (message.content && !isRichContent)) {
          <div class="">
            <!-- 消息类型（不懂/我来讲）：发送方创建消息时指定。
                 外层槽位高度取行高、并用 vertical-align:top 钉在行框上，胶囊在槽位内居中 → 与首行文字中心对齐；
                 不靠基线定位，避免字体度量不同导致 web/安卓表现不一致。整体保持 inline，正文仍绕排 -->
            @if (typeName) {
              <span class="inline-flex h-[1.6em] shrink-0 items-center align-top mr-[2px]">
                <span class="inline-flex items-center gap-[4px] px-[4px] py-[2px] rounded-[4px] bg-neutral-n5 text-[14px] text-neutral-n2">
                  <lib-fl-icon [name]="typeName | emjioIconName" [isMultiColor]="true"></lib-fl-icon>
                  @if (showTagText) {
                    <span class="w-px h-[12px] bg-neutral-n4 shrink-0"></span>
                    <span class="whitespace-pre">{{ typeName }}</span>
                  }
                </span>
              </span>
            }
            @if (message.content && !isRichContent) {
              <span class="min-w-0 flex-1 whitespace-pre-wrap break-words">{{ message.content }}</span>
            }
          </div>
        }

        <!-- 富文本正文（旧数据含 HTML）：独立一行 -->
        @if (isRichContent) {
          <div [innerHTML]="message.content | safe: 'html'"></div>
        }

        <!-- 附件 -->
        @for (url of message.mediaUrls; track url) {
          <div class="max-w-[540px]">
            @if (url | mediaType; as kind) {
              @if (kind === 'image') {
                <img [nzSrc]="url" nz-image class="w-[140px] h-[88px] object-cover rounded-[4px] block" />
              } @else if (kind === 'video') {
                <video [src]="url" controls class="max-w-full max-h-[180px] rounded-[6px] block bg-neutral-n2"></video>
              } @else if (kind === 'audio') {
                <lib-audio-player [src]="url" [config]="{ showVolume: false }" class="block"></lib-audio-player>
              } @else {
                <a [href]="url" target="_blank" class="flex items-center gap-[6px] text-primary underline break-all">
                  {{ url }}
                </a>
              }
            }
          </div>
        }

        <!-- 标签：位于消息最下方，前端分组，同 tag 聚合为人名胶囊（1 人直接显示人名，多人前 2 人…等N人）；学生发的消息只显示标签名，不显示打标签人名 -->
        @if (message.tags?.length) {
          <div class="">
            @for (group of message.tags | messageTagView; track group.tag) {
              <span class="inline-flex items-center gap-[6px] mr-[6px] px-[8px] py-[2px] rounded-[4px] bg-neutral-n5 text-[12px] text-neutral-n2">
                <lib-fl-icon [name]="group.tag | emjioIconName" [isMultiColor]="true"></lib-fl-icon>
                <span class="w-px h-[12px] bg-neutral-n4 shrink-0"></span>
                @if (message.isTeacher) {
                  <span class="whitespace-pre">{{ group.namesText }}{{ group.collapseText }}</span>
                } @else {
                  <span class="whitespace-pre">{{ group.tag }}</span>
                }
              </span>
            }
          </div>
        }
      </div>
    </div>

    <!-- 气泡外底部：本人消息只带「全班可见」标识；学生消息把「全班可见」与「回复」收进同一行，避免错落成两行 -->
    @if (isSender) {
      @if (message.toAll) {
        <div class="mt-[4px] text-[14px] text-neutral-n3">全班可见</div>
      }
    } @else {
      <div class="mt-[4px] flex w-full items-center gap-[8px]">
        @if (message.toAll) {
          <span class="text-[14px] text-neutral-n3">全班可见</span>
        }
        <button
          type="button"
          class="ml-auto cursor-pointer border-0 bg-transparent p-0 text-[14px] leading-none text-primary transition-opacity hover:opacity-75"
          (click)="onReplyClick()"
        >
          回复
        </button>
      </div>
    }
  </div>

  <!-- 正在回复该条消息时，在消息右侧显示发送时间，便于确认回复上下文 -->
  @if (replying) {
    <span class="shrink-0 self-center whitespace-nowrap text-[12px] leading-none text-neutral-n3">
      {{ message.createAt | date: 'MM/dd HH:mm' }}
    </span>
  }
</div>
